import { useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Form,
  InputNumber,
  Modal,
  Popconfirm,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { usePermissions } from '@/shared/auth/usePermissions'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { AgentCloseModal } from './AgentCloseModal'
import {
  LIMIT_KINDS,
  fetchAgentLimits,
  setAgentLimits,
  setAgentState,
  type AgentNode,
  type LimitKind,
} from './agentApi'

interface AgentGovernanceProps {
  agent: AgentNode
  /**
   * Abonent amallari chegarasi ko'rsatilsinmi.
   *
   * ⚠️ TOZA AGENTDA (`kind === 'AGENT'`) BU BO'LIM MA'NOSIZ va egasi
   * uni aynan shuning uchun olib tashlashni so'radi (2026-08-19):
   * «agentga qancha abonent biriktira oladi/bloklay oladi degan narsalar
   * kerakmas chunki agent u bilan shug'ullanmaydi». `LimitKind` ning
   * to'rttasi ham — o'chirish, nom, tarif va MAC almashtirish — sherik
   * O'Z abonenti ustida bajaradigan amallar, agentda esa o'z abonenti
   * YO'Q (ADR-029). Ya'ni bu sonlar unda hech qachon sarflanmasdi va
   * operatorga bajarilmaydigan sozlamani ko'rsatardi.
   *
   * ⚠️ Holat tugmasi (`SUSPENDED`) BUNGA KIRMAYDI — agentni to'xtatish
   * haqiqiy va tez-tez kerak bo'ladigan amal.
   */
  showLimits?: boolean
}

/** Formada har tur uchun bitta maydon; `null` = cheksiz */
type LimitForm = Record<LimitKind, number | null>

/**
 * Agentni boshqarish — holat va oylik limitlar.
 *
 * <h2>⚠️ NEGA BU KARTA KERAK BO'LDI — IKKI BO'SHLIQ, IKKALASI HAM JIMGINA</h2>
 *
 * <b>1. To'xtatish imkoni yo'q edi.</b> Backend uch holatni biladi
 * (`AgentState`): `ACTIVE`, qaytariladigan `SUSPENDED` va TERMINAL
 * `CLOSED`. Panelda esa faqat "Yopish" bor edi (`closeAgent` →
 * `DELETE` → `CLOSED`). Ya'ni "bu agentni bir oyga to'xtatib turaylik"
 * degan oddiy talab uchun operatorda faqat QAYTARIB BO'LMAYDIGAN
 * variant bor edi — va u yopgandan keyin buni bilib qolardi.
 * `POST /api/agents/{id}/state` allaqachon yozilgan edi, uni
 * chaqiradigan tugma yo'q edi.
 *
 * <b>2. Suiiste'mol chegaralari ko'rinmasdi.</b> `LimitKind` —
 * resellerning o'z abonentlari ustidagi qaytariladigan amallari
 * (o'chirish, nom almashtirish, tarif almashtirish, MAC almashtirish).
 * Ular aynan suiiste'mol uchun qulay: cheklovsiz MAC almashtirish
 * bitta hisobni ko'p qurilmaga aylantiradi va buni operator faqat
 * trafik hisobotidan, ancha keyin sezadi. Backend hisobni yuritardi va
 * limit tugaganda amalni rad etardi — lekin limitni QO'YADIGAN joy
 * bo'lmagani uchun u amalda hech qachon o'rnatilmasdi. Ya'ni himoya
 * bor edi, faqat o'chirilgan holda.
 *
 * <h2>⚠️ `null` va `0` — BOSHQA-BOSHQA NARSA</h2>
 * `null` = cheksiz, `0` = umuman ruxsat yo'q. Backend ularni hech
 * qachon bir-biriga aylantirmaydi (`MonthlyLimits` javadoc'i) va bu
 * karta ham aylantirmaydi. Bo'sh maydon "cheksiz" degani, "nol" emas —
 * shuning uchun jadvalda ular ikki xil matn bilan yoziladi.
 */
export function AgentGovernance({ agent, showLimits = true }: AgentGovernanceProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { can } = usePermissions()
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const [closing, setClosing] = useState(false)
  const [form] = Form.useForm<LimitForm>()

  const limits = useQuery({
    queryKey: queryKeys.agents.limits(agent.id),
    queryFn: () => fetchAgentLimits(agent.id),
    // Huquqsiz foydalanuvchida 403 — kutilgan javob, qayta urinish
    // faqat bekorga so'rov yuborardi.
    retry: false,
    // ⚠️ Bo'lim ko'rsatilmasa so'rov ham YUBORILMAYDI: ekranda
    // ko'rinmaydigan ma'lumot uchun har tanlovda bitta bekorchi so'rov
    // ketardi va 403 jurnalni ifloslantirardi.
    enabled: can('agent.read') && showLimits,
  })

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.agents.limits(agent.id) })
    void queryClient.invalidateQueries({ queryKey: queryKeys.agents.all })
    void queryClient.invalidateQueries({ queryKey: queryKeys.agents.children(agent.parentId ?? '') })
  }

  const changeState = useMutation({
    mutationFn: (state: 'ACTIVE' | 'SUSPENDED') => setAgentState(agent.id, state),
    onSuccess: () => {
      setError(null)
      refresh()
    },
    onError: (err) => setError(translateError(err, t)),
  })

  const saveLimits = useMutation({
    mutationFn: (values: LimitForm) => setAgentLimits(agent.id, values),
    onSuccess: () => {
      setOpen(false)
      setError(null)
      refresh()
    },
    // ⚠️ Xato bu yerda KARTAGA yozilmaydi — oyna ochiq turadi va xabar
    // uning ICHIDA chiziladi (`VoucherVoidModal` naqshi). Kartaga yozish
    // xabarni modal niqobi ortida qoldirardi: operator faqat spinner
    // to'xtaganini ko'rardi va "bosdim, hech narsa bo'lmadi" degan
    // xulosaga kelardi.
  })

  /*
    ⚠️ `CLOSED` — TERMINAL holat: u yerdan `ACTIVE` ga qaytarish yo'li
    ataylab yo'q (`AgentState` javadoc'i). Shuning uchun yopilgan
    tugunda holat tugmasi UMUMAN chizilmaydi — bosib bo'lmaydigan
    tugma ko'rsatish "urinib ko'rdim, ishlamadi" degan foydasiz
    tajribani beradi.

    ⚠️ SHU SABABDAN «Yopish» AYRIM TUGMA. Uni `nextState` zanjiriga
    qo'shish mumkin EMAS edi: `ACTIVE ⇄ SUSPENDED` — qaytariladigan
    almashtirish, yopish esa BOSHQA turdagi amal. Ikkalasini bitta
    tugmaga yig'ish "to'xtatmoqchi edim, yopib qo'ydim" holatini
    yaratardi — va uni qaytarib bo'lmasdi.
  */
  const canToggleState = agent.state !== 'CLOSED'
  const nextState = agent.state === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE'

  /**
   * Joriy limitlar MA'LUMmi — ya'ni ular ustiga yozish xavfsizmi.
   *
   * ⚠️ Shart aynan `isSuccess`, `!isPending` EMAS va bu farq MA'LUMOT
   * YO'QOTISH darajasida (`useSiteIndex.reliable` bilan bir xil
   * mulohaza). `!isPending` so'rov YIQILGANDA ham rost bo'ladi; o'shanda
   * `limits.data` — `undefined`, forma to'rtala maydonni BO'SH ochadi,
   * "Saqlash" esa to'rtalasiga `null` yuboradi. `null` bu yerda "cheksiz"
   * degani (`setAgentLimits` javadoc'i: kalit YO'QLIGI "tegilmaydi",
   * `null` esa "cheksiz"), ya'ni MUVAFFAQIYATSIZ O'QISH BARCHA
   * LIMITLARNI JIMGINA O'CHIRARDI. Limitlar reseller suiiste'moliga
   * qarshi yagona to'siq — ular jimgina o'chsa himoya ham yo'qoladi.
   *
   * Huquqsiz foydalanuvchida so'rov umuman ketmaydi (`enabled`), ya'ni
   * `isSuccess` yolg'on qoladi va tugma o'sha holatda ham yopiq bo'ladi.
   */
  const limitsReliable = limits.isSuccess

  const openLimits = () => {
    // ⚠️ Ikkinchi qorovul: tugma yopiq bo'lsa ham (klaviatura, eski
    // chizilish) forma ISHONCHSIZ ma'lumot bilan ochilmasin.
    if (!limitsReliable || !limits.data) return
    // Forma joriy qiymatlar bilan to'ldiriladi — bo'sh forma
    // "hammasi cheksiz" degan YOLG'ON boshlang'ich holat bo'lardi va
    // saqlash tugmasi mavjud limitlarni o'chirib yuborardi.
    const current = limits.data
    form.setFieldsValue(Object.fromEntries(LIMIT_KINDS.map((k) => [k, current.limits[k] ?? null])))
    setOpen(true)
  }

  /** Bekor qilib chiqish — xato xabari ham qolmasin (`VoucherVoidModal` naqshi) */
  const closeLimits = () => {
    saveLimits.reset()
    setOpen(false)
  }

  return (
    <Card
      title={t('agentGovernance.title')}
      extra={
        <Space size={4}>
          {canToggleState ? (
            <Can permission="agent.create">
              <Popconfirm
                title={t(`agentGovernance.confirm.${nextState}`)}
                description={t(`agentGovernance.confirmHint.${nextState}`)}
                okText={t(`agentGovernance.action.${nextState}`)}
                cancelText={t('common.cancel')}
                onConfirm={() => changeState.mutate(nextState)}
              >
                <Button size="small" danger={nextState === 'SUSPENDED'} loading={changeState.isPending}>
                  {t(`agentGovernance.action.${nextState}`)}
                </Button>
              </Popconfirm>
            </Can>
          ) : null}
          {canToggleState ? (
            <Can permission="agent.create">
              {/* ⚠️ Tasdiq `Popconfirm` EMAS: amal qaytarilmaydi va
                  oqibati ikkita O'LCHANADIGAN songa bog'liq (ostidagi
                  tugunlar va abonentlar). Ular alohida so'rov bilan
                  olinadi va oynada ko'rsatiladi — `AgentCloseModal`. */}
              <Button size="small" danger onClick={() => setClosing(true)}>
                {t('agents.close.action')}
              </Button>
            </Can>
          ) : null}
          {showLimits ? (
            <Can permission="agent.create">
              {/* Yopiq tugmaning SABABI aytiladi — aks holda u shunchaki
                  "ishlamayotgan tugma" bo'lib ko'rinardi */}
              <Tooltip title={limitsReliable ? undefined : t('agentGovernance.limitsUnavailable')}>
                <Button size="small" onClick={openLimits} disabled={!limitsReliable}>
                  {t('agentGovernance.setLimits')}
                </Button>
              </Tooltip>
            </Can>
          ) : null}
        </Space>
      }
    >
      {error ? (
        <Alert
          type="error"
          showIcon
          closable
          message={error}
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {agent.state === 'SUSPENDED' ? (
        /*
          ⚠️ To'xtatilgan agent teg bilan cheklanmaydi — SABABI ham
          yoziladi. "SUSPENDED" so'zi o'zi nima taqiqlanganini
          aytmaydi, operator esa aynan shuni bilishi kerak: pul amali
          o'tmaydi (`AgentState.canTransact()`), daraxt esa joyida.
        */
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('agentGovernance.suspendedTitle')}
          description={t('agentGovernance.suspendedHint')}
        />
      ) : null}

      {agent.state === 'CLOSED' ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('agentGovernance.closedTitle')}
          description={t('agentGovernance.closedHint')}
        />
      ) : null}

      <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
        {showLimits ? t('agentGovernance.limitsExplain') : t('agentGovernance.agentNoLimits')}
      </Typography.Paragraph>

      {!showLimits ? null : limits.isError ? (
        /* ⚠️ Yiqilgan so'rov "limit yo'q" bo'lib KO'RINMASLIGI kerak:
           jadval o'rniga xato va uning oqibati yoziladi. */
        <Alert
          type="warning"
          showIcon
          message={translateError(limits.error, t)}
          description={t('agentGovernance.limitsUnavailable')}
        />
      ) : (
        <Descriptions column={1} size="small" bordered>
          {LIMIT_KINDS.map((kind) => {
            const limit = limits.data?.limits[kind] ?? null
            const used = limits.data?.used[kind] ?? 0
            return (
              <Descriptions.Item key={kind} label={t(`agentGovernance.limitKind.${kind}`)}>
                {limits.isPending ? (
                  '…'
                ) : limit === null ? (
                  <Space size={4}>
                    <Tag>{t('agentGovernance.unlimited')}</Tag>
                    <Typography.Text type="secondary">
                      {t('agentGovernance.usedThisMonth', { used })}
                    </Typography.Text>
                  </Space>
                ) : (
                  <Space size={4}>
                    {/* 0 — "umuman ruxsat yo'q", "sozlanmagan" EMAS.
                        Ikkalasini bir xil ko'rsatish agentni jimgina
                        to'liq bloklab qo'yardi va sababi topilmasdi. */}
                    <Typography.Text type={limit === 0 ? 'danger' : undefined} strong>
                      {used} / {limit}
                    </Typography.Text>
                    {limit === 0 ? <Tag color="red">{t('agentGovernance.forbidden')}</Tag> : null}
                    {limit > 0 && used >= limit ? (
                      <Tag color="orange">{t('agentGovernance.exhausted')}</Tag>
                    ) : null}
                  </Space>
                )}
              </Descriptions.Item>
            )
          })}
        </Descriptions>
      )}

      <Modal
        open={open}
        title={t('agentGovernance.setLimits')}
        onCancel={closeLimits}
        onOk={() => submitOnValid(form, (values) => saveLimits.mutate(values))}
        confirmLoading={saveLimits.isPending}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        destroyOnHidden
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('agentGovernance.emptyMeansUnlimited')}
          description={t('agentGovernance.emptyMeansUnlimitedHint')}
        />

        {/* Xato SHU YERDA qoladi — oyna ochiq, ya'ni operator xabarni
            amal tugmasi bilan bir ekranda ko'radi */}
        {saveLimits.isError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={translateError(saveLimits.error, t)}
          />
        ) : null}

        <Form form={form} layout="vertical" preserve={false} disabled={saveLimits.isPending}>
          {LIMIT_KINDS.map((kind) => (
            <Form.Item key={kind} name={kind} label={t(`agentGovernance.limitKind.${kind}`)}>
              <InputNumber
                min={0}
                precision={0}
                style={{ width: '100%' }}
                placeholder={t('agentGovernance.unlimited')}
              />
            </Form.Item>
          ))}
        </Form>
      </Modal>

      <AgentCloseModal open={closing} agent={agent} onClose={() => setClosing(false)} />
    </Card>
  )
}
